import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRightIcon, CheckIcon, CopyIcon } from "lucide-react";
import { EYEBROW } from "../utils/typography";
const EMAIL = "hello@alyssaretza.com";
const socials = [{
  label: "LinkedIn",
  href: "https://www.linkedin.com/in/alyssa-retza/"
}];
type CopyState = "idle" | "copied" | "error";
export function ContactFooter() {
  const [state, setState] = useState<CopyState>("idle");
  const timer = useRef<number>();
  const reduce = useReducedMotion();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  async function copyEmail() {
    window.clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(EMAIL);
      setState("copied");
    } catch {
      setState("error");
    }
    timer.current = window.setTimeout(() => setState("idle"), 2400);
  }
  const status = state === "copied" ? {
    icon: <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-205" data-mp="3qvte7nypireb" />,
    text: "Copied to clipboard"
  } : state === "error" ? {
    icon: null,
    text: "Couldn’t copy — select the address to copy it"
  } : {
    icon: <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-206" data-mp="2u2e4y3kw6a18" />,
    text: "Click to copy"
  };
  return <footer id="contact" className="w-full bg-espresso px-6 py-20 text-cream md:px-12 lg:px-20 lg:py-28" data-id="element-207" data-mp="2pbpe8qh1dh35">
      <p className={`${EYEBROW} text-mustard`} data-id="element-208" data-mp="3402a7ng4izxn">Curious? Let’s talk.</p>

      <button type="button" onClick={copyEmail} aria-describedby="email-status" className={`group mt-6 inline-flex items-center gap-4 text-left text-[clamp(2.25rem,7vw,6.5rem)] font-medium leading-none tracking-[-0.02em] transition-colors duration-150 ease-out hover:text-mustard focus-visible:text-mustard focus-visible:rounded-xl focus-visible:outline-offset-8`} data-id="element-209" data-mp="342ep1rql4u6h">
        {EMAIL}
        <span className="relative inline-flex h-[0.6em] w-[0.6em] shrink-0" data-id="element-210" data-mp="1em5k26f8q3d8">
          <AnimatePresence initial={false} mode="popLayout" data-id="element-211" data-mp="3dqf4uw48tvpf">
            {state === "copied" ? <motion.span key="check" className="absolute inset-0 text-mustard" initial={reduce ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-212" data-mp="3ipibum3sac01">
                <CheckIcon className="h-full w-full" data-id="element-213" data-mp="hsvaqnt30c4" />
              </motion.span> : <motion.span key="arrow" className="absolute inset-0" initial={reduce ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-214" data-mp="5gvp1eyc5k4s">
                <ArrowUpRightIcon className="h-full w-full transition-transform duration-200 ease-out group-hover:-translate-y-1 group-hover:translate-x-1" data-id="element-215" data-mp="oahqmu7kvzn5" />
              </motion.span>}
          </AnimatePresence>
        </span>
      </button>

      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm" data-id="element-216" data-mp="1zqsr7q2eudjs">
        <p id="email-status" role="status" aria-live="polite" className={`inline-flex items-center gap-2 transition-colors duration-150 ${state === "copied" ? "text-mustard" : "text-cream/60"}`} data-id="element-217" data-mp="1zswuq6wedajj">
          {status.icon}
          {status.text}
        </p>
      </div>

      <div className="mt-20 flex flex-col gap-6 border-t border-cream/10 pt-8 text-sm text-cream/60 md:flex-row md:items-center md:justify-between" data-id="element-218" data-mp="2ip4zl9lhzjeb">
        <p data-id="element-219" data-mp="2dqbotoxli6hu">© 2026 Alyssa Retza. Appleton, Wisconsin.</p>
        <ul className="flex gap-6" data-id="element-220" data-mp="3b68zql3w7z7a">
          {socials.map(s => <li key={s.label} data-id="element-221" data-mp="36f95xoq2lx90">
              <a href={s.href} {...s.href.startsWith("http") ? {
            target: "_blank",
            rel: "noopener noreferrer"
          } : {}} className="transition-colors duration-150 hover:text-cream" data-id="element-222" data-mp="36ezgc28xts3u">
                {s.label}
              </a>
            </li>)}
        </ul>
      </div>
    </footer>;
}